import { FilmChapter } from '../film/film.types';

/**
 * "Ultimate Cinema Seating": a tour of a multiplex auditorium fitted with
 * Little Nap recliners. Pure data (see film.types.ts for what the camera
 * numbers mean) — what the seats do in each chapter lives in
 * CinemaScene.animate().
 *
 * The auditorium's front row stands at the origin facing the screen (+Z);
 * rows step up and back towards -Z.
 */
export interface CinemaChapter extends FilmChapter {
  id: 'intro' | 'recline' | 'backrest' | 'table' | 'layout' | 'screen';
}

export const CINEMA_CHAPTERS: CinemaChapter[] = [
  {
    id: 'intro',
    nav: 'Cinema',
    kicker: 'Multiplex recliners',
    title: 'Ultimate Cinema Seating',
    body: 'Make intelligent use of spaces — recliners that give your audience the VIP experience.',
    duration: 8,
    cut: true,
    cam: {
      theta: 14, phi: 74, span: 4.3, target: [0, 1.3, -3.2], dTheta: -24, dSpan: -0.5,
      tall: { span: 2.1, dSpan: -0.2 },
    },
    hotspots: [],
  },
  {
    id: 'recline',
    nav: 'Recline',
    kicker: 'Space-savvy design',
    title: 'Motorised to fit snugly, even in tight spaces',
    body: 'Motorised recliners crafted for tight rows — without compromising on style or comfort.',
    duration: 8,
    cam: {
      theta: 40, phi: 72, span: 2.7, target: [0.2, 0.65, 0.15], dTheta: -18,
      tall: { span: 1.45, target: [0.9, 0.65, 0.15] },
    },
    hotspots: [{ id: 'motor', label: 'Motorised recline', anchor: 'foot', side: 'r', at: 0.5 }],
  },
  {
    id: 'backrest',
    nav: 'Backrest',
    kicker: 'Backrest cushioning',
    title: 'Comfort that supports and soothes',
    body: 'Customisable backrest cushioning for ergonomic support and supreme comfort.',
    duration: 8,
    cam: {
      theta: 52, phi: 64, span: 1.3, spanV: 0.8, target: [0.5, 0.72, 0.05], dTheta: -20,
      tall: { span: 0.95 },
    },
    hotspots: [
      { id: 'backrest', label: 'Backrest cushioning', anchor: 'back', side: 'l', at: 0.2 },
      { id: 'cup', label: 'Cup holder', anchor: 'cup', side: 'r', at: 0.32 },
    ],
  },
  {
    id: 'table',
    nav: 'Tables',
    kicker: 'Smart swivel tables',
    title: 'Tables that spin a clever 90°',
    body: 'Designed for convenience — they swing out of the way to avoid collisions.',
    duration: 8,
    cam: {
      theta: -26, phi: 56, span: 1.25, spanV: 0.8, target: [0.5, 0.8, 0.2], dTheta: -18,
      tall: { span: 0.95 },
    },
    hotspots: [{ id: 'tray', label: 'Spins 90°', anchor: 'tray', side: 'r', at: 0.42 }],
  },
  {
    id: 'layout',
    nav: 'Layout',
    kicker: '3D layout assistance',
    title: 'Planned to the last seat',
    body: 'Comprehensive 3D layouts, detailed drawings and expert consultation — optimising comfort and maximising legroom.',
    duration: 8,
    cam: {
      theta: -10, phi: 14, span: 6.2, spanV: 5.4, target: [0, 0.8, -3.6], dTheta: 20, dPhi: 6,
      tall: { span: 3.4, spanV: 5.4 },
    },
    hotspots: [
      { id: 'legroom', label: 'Maximised legroom', anchor: 'legroom', side: 'r', at: 0.28 },
      { id: 'custom', label: 'Customised layouts', anchor: 'block', side: 'l', at: 0.4 },
    ],
  },
  {
    id: 'screen',
    nav: 'Screen',
    kicker: 'On 1,300+ screens',
    title: 'The best cinema experience starts here',
    body: 'Upgrade your cinema with recliners made to order for your auditorium.',
    duration: 7,
    cut: true,
    cam: {
      theta: 174, phi: 86, span: 6.2, spanV: 2.6, target: [0, 2.5, 8.5], dTheta: 10, dSpan: -0.9,
      tall: { span: 3.4, dSpan: -0.3 },
    },
    hotspots: [],
  },
];
